import React, { useEffect, useRef, useState } from "react";
import { ActivityIndicator, StyleSheet, View } from "react-native";

declare global {
  interface Window {
    pdfjsLib?: any;
  }
}

type Props = { src: string; maxWidth?: number; loading?: boolean; dirty?: boolean };

export function PdfCanvas({ src, maxWidth = 900, loading = false, dirty = false }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!window.pdfjsLib || !containerRef.current) return;

      try {
        const pdf = await window.pdfjsLib.getDocument({ url: src }).promise;
        if (cancelled) return;

        // clear old
        containerRef.current.innerHTML = "";

        for (let p = 1; p <= pdf.numPages; p++) {
          const page = await pdf.getPage(p);
          const viewport = page.getViewport({ scale: 1 });
          // scale to fit maxWidth
          const scale = Math.min(1, maxWidth / viewport.width);
          const vp = page.getViewport({ scale });
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d")!;
          canvas.width = vp.width;
          canvas.height = vp.height;
          canvas.style.display = "flex";
          canvas.style.margin = "0 auto 0px";

          containerRef.current!.appendChild(canvas);
          await page.render({ canvasContext: ctx, viewport: vp }).promise;
        }

        setReady(true);
      } catch (e) {
        console.error("PDF render failed:", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [src, maxWidth]);

  // fallback if CDN not loaded
  if (!window.pdfjsLib) {
    return (
      <View style={{ minWidth: "100%", minHeight: "100%" }}>
        <iframe src={src} style={{ width: "100%", height: "100%", border: "none" }} />
        {loading && <ActivityIndicator size={80} style={styles.activityIndicator} />}
      </View>
    );
  }

  return (
    <View>
      <div ref={containerRef} style={{ width: "100%", maxWidth, margin: "0 auto" }}>
        {!ready && <div style={{ padding: 12, opacity: 0.6 }}>Loading PDF…</div>}
      </div>
      {loading && <ActivityIndicator size={80} style={styles.activityIndicator} />}
    </View>
  );
}

const styles = StyleSheet.create({
  activityIndicator: {
    backgroundColor: "rgba(0,0,0,0.4)",
    borderTopWidth: 1,
    position: "absolute",
    top: 0,
    height: "100%",
    bottom: 0,
    left: 0,
    right: 0,
    flex: 1,
  },
});
